import { Select, Icon, Loader } from '@pwc/pwc-deals-ui';
import { Constants } from '@pwc/pwc-deals-web-common';
import { useSelector } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';
import { getReportEmbedDataByReportId } from '../../analyses/powerBiService';
import './PowerBiPageIngestion.scss';
import getPowerBiEmbedConfigurationBase from './getPowerBiEmbedConfigurationBase';
import { getLibraryItemsByType } from '../libraryItemService';
import { getIsDarkModeSelector } from '../../../common/redux/userSettings/userSettingsSlice';
import { getPowerBiTheme } from './getPowerBiTheme';

const retrievePages = async (
  projectId,
  report,
  selectedReportId,
  setPagesHandler,
  setIsLoading
) => {
  const allReportPages = await report.getPages();
  const allIngestedPageLibraryItems = await getLibraryItemsByType(
    projectId,
    Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT_PAGE.value
  );
  const ingestedPageNamesWithReportId = allIngestedPageLibraryItems.map(
    (page) => {
      const { ReportId, PageName } = JSON.parse(page.data);
      return `${ReportId} - ${PageName}`;
    }
  );
  const pagesNotIngested = allReportPages.filter(
    (page) =>
      !ingestedPageNamesWithReportId.includes(
        `${selectedReportId} - ${page.name}`
      )
  );

  setPagesHandler(pagesNotIngested);
  setIsLoading(false);
};

const PowerBiPageIngestion = ({
  projectId,
  selectedReportId,
  reportOptions,
  setSelectedReportId,
  selectedPage,
  setSelectedPage,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [embeddedData, setEmbeddedData] = useState('');
  const powerBiContainerRef = useRef(null);
  const [pages, setPages] = useState([]);
  const [selectedPageName, setSelectedPageName] = useState('');
  const isDarkMode = useSelector(getIsDarkModeSelector);

  const mapPageOptions = (pagesToMap) => {
    return pagesToMap.map((page) => {
      return {
        label: `${page.displayName}`,
        value: page.name,
      };
    });
  };

  useEffect(() => {
    setSelectedPage({});
    async function fetchReportData() {
      if (selectedReportId) {
        setIsLoading(true);
        const data = await getReportEmbedDataByReportId(
          projectId,
          selectedReportId
        );
        setEmbeddedData(data);
        if (data && selectedReportId) {
          const config = getPowerBiEmbedConfigurationBase({
            accessToken: data.embedToken,
            embedUrl: data.embedUrl,
            id: selectedReportId,
            theme: getPowerBiTheme(isDarkMode),
          });
          if (powerBiContainerRef.current) {
            const report = window.powerbi.load(
              powerBiContainerRef.current,
              config
            );
            report.on('loaded', () =>
              retrievePages(
                projectId,
                report,
                selectedReportId,
                setPages,
                setIsLoading
              )
            );
          }
        }
      }
    }

    fetchReportData();
  }, [projectId, selectedReportId, setSelectedPage, isDarkMode]);

  const handlePageOnChange = (pageName) => {
    setSelectedPageName(pageName);
    if (pageName === '') {
      setSelectedPage({});
    } else {
      const { name, displayName } = pages.find((p) => p.name === pageName);

      const config = getPowerBiEmbedConfigurationBase({
        accessToken: embeddedData.embedToken,
        embedUrl: embeddedData.embedUrl,
        id: selectedReportId,
        pageName: name,
        theme: getPowerBiTheme(isDarkMode),
      });

      if (powerBiContainerRef.current) {
        window.powerbi.embed(powerBiContainerRef.current, config);
      }

      setSelectedPage({
        name,
        displayName,
      });
    }
  };

  const selectReport = (value) => {
    if (value === '') {
      setIsLoading(false);
    }
    setSelectedPageName('');
    setPages([]);
    setSelectedReportId(value);
  };

  const isPageSelectDisabled = !selectedReportId || pages.length === 0;

  return (
    <div className="power-bi-report-select">
      <div className="report-select">
        <Select
          id="power-bi-report-select"
          data-testid="power-bi-report-select"
          field={{ name: 'power-bi-report-select' }}
          basic
          label="Report name"
          placeholder="Select report"
          options={reportOptions}
          value={selectedReportId}
          onChange={(event) => selectReport(event.target.value)}
          characterLimit={49}
        />
      </div>
      {isLoading && <Loader dataTestId="power-bi-reports-loader" />}
      {!isLoading &&
        (pages.length === 0 && selectedReportId ? (
          <span className="no-reports-span">
            No more pages for the selected Power BI Report to ingest
          </span>
        ) : (
          <div className="page-select">
            <Select
              id="power-bi-page-select"
              data-testid="power-bi-page-select"
              label="Tab"
              basic
              disabled={isPageSelectDisabled}
              placeholder="Select tab"
              field={{ name: 'power-bi-page-select' }}
              options={mapPageOptions(pages)}
              value={selectedPage.name}
              onChange={({ target: { value } }) => handlePageOnChange(value)}
              characterLimit={40}
            />
          </div>
        ))}
      <div
        className={`${
          selectedPageName === ''
            ? 'hidden-power-bi-hint'
            : 'visible-power-bi-hint'
        }`}
      >
        <div className="import-hint">
          <Icon
            name="information"
            className="fill icon-yellow"
            width={16}
            data-testid="hint-icon"
          />
          <p>To resize the tab, use the options available in Power BI.</p>
        </div>
      </div>
      <div
        className={`${
          selectedPageName === ''
            ? 'hidden-power-bi-container'
            : 'visible-power-bi-container'
        }`}
        data-testid="power-bi-embed-container"
        ref={powerBiContainerRef}
      />
    </div>
  );
};

export default PowerBiPageIngestion;
